// pages/Dashboard.tsx
import React, { useState, useEffect, useMemo, Fragment } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "wouter";
import Layout from "@/components/Layout";
import SEO from '@/components/SEO'; // SEO: dashboard private -> noindex,nofollow
import ReasoningStructuredView from "@/components/ReasoningStructuredView";
import { getReasoningSectionLabels } from "@/lib/reasoningParser";
import { MarketOverview } from "@/components/MarketOverview";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table, TableHeader, TableHead, TableRow, TableBody, TableCell
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Eye, Activity, Coins, RotateCcw,
  Search, BarChart3, AlertCircle, X, Loader2,
  ArrowUp, ArrowDown, Minus, Clock, TrendingUp, Shield, LineChart
} from "lucide-react";
import { Copy, Check } from "lucide-react";
// (framer-motion import removed after removing AI Context toggle)
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { ShimmerBlock } from "@/components/ui/shimmer";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { useTransactions } from "@/hooks/useTransactions";
import { useActiveAnalyses, useAnalysisDetail, useLatestReasoning } from "@/hooks/useAnalysis";
import type { TransactionItem } from "@/types/transactions";

/* ---------- Local Extended Types (fixing missing props from active analyses) ---------- */
interface AnalysisListItemLite {
  id?: number;
  symbol?: string;
  status?: string;
  created_at?: string;
  progress_percent?: number;
  request_id?: number;
  analysis_request_id?: number;
  latest_reasoning?: { reason?: string } | null;
  next_run_at?: string | null;
  time_remaining_seconds?: number | null;
}

type APISignal = {
  id: number;
  symbol: string;
  signal_type?: string;
  signalType?: string;
  current_status?: string;
  status?: string;
  confidence_percent?: number;
  confidence?: number;
  generated_at?: string;
  created_at?: string;
  request_id?: number;
  analysis_request_id?: number;
  entry_price?: number;
  take_profit?: number; // legacy, keep as fallback
  tp1?: number;
  tp2?: number;
  stop_loss?: number;
  rr_ratio?: number;
  timeframe?: string;
  reasoning?: string | null;
  entry_timing?: string;
  is_active?: boolean;
};

type SignalRow = {
  id: number;
  symbol: string;
  signalType: "LONG" | "SHORT" | "NO_TRADE";
  confidence?: number;
  status: string;
  created_at: string;
  entry?: number | string;
  tp?: number | string; // legacy single TP display
  tp1?: number | string;
  tp2?: number | string;
  sl?: number | string;
  rr?: number | string;
  timeframe?: string;
  used_credits?: number;
  request_id?: number;
  reasoning?: string;
  _loadingDetail?: boolean;
  is_active?: boolean;
  _merged?: boolean;
  _reasonTried?: boolean;
  _live?: boolean;
  analysis_id?: number;
  progress_percent?: number;
  next_run_at?: string | null; // <-- added
  time_remaining_seconds?: number | null;
};

const TIME_FILTERS = [
  { value: "all", label: "All Time" },
  { value: "24h", label: "Last 24H" },
  { value: "7d", label: "Last 7D" },
  { value: "30d", label: "Last 30D" },
];

/* ---------- Utility: Time Range ---------- */
function isWithinRange(ts: string, range: string) {
  if (!ts) return false;
  if (range === "all") return true;
  const t = new Date(ts).getTime();
  if (isNaN(t)) return false;
  const now = Date.now();
  const day = 86400000;
  if (range === "24h") return now - t <= day;
  if (range === "7d") return now - t <= 7 * day;
  if (range === "30d") return now - t <= 30 * day;
  return true;
}

/* ---------- React Query Wrappers ---------- */
function useSignalsList() {
  return useQuery<APISignal[]>({
    queryKey: ["/api/signals/"],
    retry: (count, err: any) => {
      const msg = String(err?.message || "");
      if (msg.startsWith("__TRANSIENT_HTML__")) return count < 8; // ~40s with 5s refetchInterval
      if (msg.startsWith("__TRANSIENT_PARSE__")) return count < 3;
      return false;
    },
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/signals/");
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (ct.includes("application/json")) {
        try { return await res.json(); } catch (e:any) {
          console.warn('[signals json parse]', e);
          throw new Error(`__TRANSIENT_PARSE__ ${e?.message||e}`);
        }
      }
      const clone = res.clone();
      try { return await res.json(); } catch {
        const text = await clone.text();
        if (/<!DOCTYPE|<html|<head|<body/i.test(text)) {
          throw new Error(`__TRANSIENT_HTML__ ${text.slice(0,80).replace(/\n/g,' ')}`);
        }
        throw new Error(text.slice(0,160));
      }
    },
    refetchInterval: 5000
  });
}

function useSignalDetail(id: number | null) {
  return useQuery<APISignal | undefined>({
    queryKey: ["/api/signals", id],
    enabled: id != null,
    queryFn: async () => {
      if (id == null) return undefined;
      const res = await apiRequest("GET", `/api/signals/${id}`);
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    // Inherit always-fresh defaults
  });
}

// New: aggregated token usage (spent & refunded)
function useCreditsUsage() {
  return useQuery<{
    spent?: number;          // total permanently spent
    refunded?: number;       // total tokens returned
    used?: number;           // (fallback key some APIs might use)
    returns?: number;        // (fallback key)
  }>({
    queryKey: ["/api/credits/usage"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/credits/usage");
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    refetchInterval: 10000, // keep reasonably fresh
  });
}

/* ---------- Helpers for analysis objects ---------- */
function extractAnalysisId(a: AnalysisListItemLite | any): number | undefined {
  if (!a || typeof a !== "object") return undefined;
  if (typeof a.id === "number") return a.id;
  if (typeof a.request_id === "number") return a.request_id;
  if (typeof a.analysis_request_id === "number") return a.analysis_request_id;
  return undefined;
}

function extractReason(a: AnalysisListItemLite | any): string {
  return a?.latest_reasoning?.reason || "";
}

function extractProgress(a: AnalysisListItemLite | any): number | undefined {
  return a?.progress_percent;
}

function isFailedDashboardStatus(value: any): boolean {
  const raw = String(value ?? '').toUpperCase();
  return raw === 'FAILED' || raw.includes('ERROR') || raw.includes('CANCEL');
}

/* ---------- Mapping Signals to Rows ---------- */
function mapToRow(s: APISignal, idx: number): SignalRow {
  const rawType = (s.signalType || s.signal_type || "").toUpperCase();
  let signalType: SignalRow["signalType"] = "NO_TRADE";
  if (rawType === "LONG" || rawType === "BUY") signalType = "LONG";
  else if (rawType === "SHORT" || rawType === "SELL") signalType = "SHORT";
  else if (rawType.includes("NO") || rawType.includes("NEUTRAL")) signalType = "NO_TRADE";

  const conf = s.confidence ?? s.confidence_percent;
  return {
    id: s.id ?? idx + 1,
    symbol: (s.symbol || "").toUpperCase(),
    signalType,
    confidence: typeof conf === "number"
      ? (conf > 0 && conf <= 1 ? Math.round(conf * 100) : Math.round(conf))
      : undefined,
    status: (s.status || s.current_status || "UNKNOWN").toUpperCase(),
    created_at: s.created_at || s.generated_at || "",
    entry: s.entry_price,
    // Prefer new tp1/tp2; keep legacy `tp` for existing UI until fully migrated
    tp: s.take_profit,
    tp1: (s as any).tp1 ?? s.take_profit,
    tp2: (s as any).tp2 ?? undefined,
    sl: s.stop_loss,
    rr: s.rr_ratio,
    timeframe: s.timeframe,
    request_id: s.request_id || s.analysis_request_id,
    reasoning: s.reasoning || undefined,
  is_active: s.is_active,
  next_run_at: (s as any).next_run_at || null
  };
}

// Neutral display labels for analysis direction
function displaySignalLabel(t: SignalRow["signalType"], isLive?: boolean) {
  if (isLive && t === "NO_TRADE") return "—";
  if (t === "LONG") return "Bullish";
  if (t === "SHORT") return "Bearish";
  return "Neutral";
}

/* ---------- Live Progress Countdown Helpers ---------- */
/*
  We ბილდ an ETA countdown similar to SignalDetail "Live Analysis".
  Assumptions:
    - progress_percent increases roughly linearly.
    - We estimate totalDuration = elapsed / (progress/100).
    - Remaining = totalDuration - elapsed (clamped).
  If progress < 2% we just show "Initializing..."
*/
// (Removed legacy heuristic countdown hooks; using direct server time & velocity estimation)

/* ---------- Status Classification Helpers (restored) ---------- */
const IN_PROGRESS_PATTERNS = [
  /HOLD/, /OPEN/, /ACTIVE/, /IN[_ ]?PROGRESS/, /PENDING/,
  /GENERAT/, /PROCESS/, /BUILD/, /RUNNING/
];
const COMPLETED_PATTERNS = [
  /FINAL/, /FINISH/, /FINISHED/, /COMPLET/, /DONE/, /CLOSED/, /SUCCESS/, /READY/
];
function classifyStatusRaw(raw: string): "in_progress" | "completed" | "other" {
  const r = (raw || "").toUpperCase();
  if (COMPLETED_PATTERNS.some(rx => rx.test(r))) return "completed";
  if (IN_PROGRESS_PATTERNS.some(rx => rx.test(r))) return "in_progress";
  return "other";
}
// param version (no external closure on activeRequestIds)
function effectiveGroup(
  s: SignalRow,
  activeSet: Set<number>
): "live" | "in_progress" | "completed" | "other" {
  if (s._live) return "live";
  const base = classifyStatusRaw(s.status);
  const reqId = s.request_id;
  const isActiveReq = reqId != null && activeSet.has(reqId);
  if (base === "in_progress" && isActiveReq) return "live";
  if (base === "completed") return "completed";
  if (base === "in_progress" && !isActiveReq) return "completed";
  return "other";
}

function normalizeDashboardNoTradeReasoning(text?: string | null): string {
  const t = String(text ?? "").trim();
  if (!t) return "";

  // Legacy NO_TRADE text (from backend) -> clean, professional copy for dashboard display.
  const markers: RegExp[] = [
    /NO[_ ]?TRADE:\s*Volume\s+too\s+low/i,
    /Action:\s*NO\s*TRADE/i,
    /NO\s*TRADE\s*-\s*Poor\s+market\s+conditions/i,
    /Flat\s+market\s+conditions/i,
    /Weekend\s*-\s*low\s+liquidity\s+expected/i,
  ];
  const hits = markers.reduce((n, rx) => (rx.test(t) ? n + 1 : n), 0);
  if (hits >= 2) {
    return [
      "Market Status: Low participation",
      "Market Condition: Environment not suitable for analysis deployment",
      "Price behavior: Flat / inactive",
      "Session note: Weekend liquidity is typically reduced",
    ].join("\n");
  }

  return t;
}

/* ---------- Component ---------- */
export default function Dashboard() {
  const seoNode = (
    <SEO
      title="Dashboard | TradeSagePro"
      description="Analyze AI market research outputs, monitor live analyses, and manage tokens in your private dashboard."
      canonical="https://tradesagepro.com/dashboard"
      noindex
      nofollow
    />
  );
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const signalsQuery = useSignalsList();
  const activeAnalysesQuery = useActiveAnalyses();
  const usageQuery = useCreditsUsage();

  // NEW: local filter & detail state
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "BULLISH" | "BEARISH" | "NEUTRAL">("all");
  const [timeFilter, setTimeFilter] = useState<"all" | "24h" | "7d" | "30d">("all");
  const [detail, setDetail] = useState<SignalRow | null>(null);
  const [detailExpanded, setDetailExpanded] = useState(false);

  const goToTrading = () => {
    setDetail(null);
    setLocation("/trading");
  };

  // Auth redirect notice (makes toast & setLocation used)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to access your dashboard.",
        variant: "destructive"
      });
      setLocation("/login");
    }
  }, [authLoading, isAuthenticated, toast, setLocation]);

  // NEW: restore transactions (for fallback)
  const transactionsQuery = useTransactions();
  const transactions: TransactionItem[] = Array.isArray(transactionsQuery.data) ? transactionsQuery.data : [];

  const signalsLoading = signalsQuery.isLoading;
  const signalsError = signalsQuery.isError ? signalsQuery.error : null;

  const baseSignals: SignalRow[] = (signalsQuery.data || [])
    .filter((item) => !isFailedDashboardStatus(item?.status || item?.current_status))
    .map(mapToRow);

  const activeAnalyses: AnalysisListItemLite[] = Array.isArray(activeAnalysesQuery.data)
    ? activeAnalysesQuery.data as any
    : [];

  const activeAnalysesMap = useMemo(() => {
    const m = new Map<number, AnalysisListItemLite>();
    activeAnalyses.forEach(a => {
      const key = extractAnalysisId(a);
      if (typeof key === "number") m.set(key, a);
    });
    return m;
  }, [activeAnalyses]);

  // (Ensure activeRequestIds is declared before anything uses it)
  const activeRequestIds = useMemo(() => {
    return new Set(
      activeAnalyses
        .map(a => extractAnalysisId(a))
        .filter((v): v is number => typeof v === "number")
    );
  }, [activeAnalyses]);

  // Build synthetic live rows
  const signalReqIds = new Set(
    baseSignals.map(s => s.request_id).filter((v): v is number => !!v)
  );

  const liveRows: SignalRow[] = activeAnalyses
    .filter(a => {
      const rid = extractAnalysisId(a);
      if ((a as any).status === "COMPLETED") return false;
      if (isFailedDashboardStatus((a as any).status)) return false;
      if (rid == null) return true;
      return !signalReqIds.has(rid);
    })
    .map(a => {
      const rid = extractAnalysisId(a) ?? Date.now();
      return {
        id: -rid,
        symbol: (a.symbol || "").toUpperCase(),
        signalType: "NO_TRADE",
        status: ((a as any).status || "IN_PROGRESS").toUpperCase(),
        created_at: a.created_at || new Date().toISOString(),
        request_id: rid,
        reasoning: extractReason(a),
        _live: true,
        analysis_id: rid,
        progress_percent: extractProgress(a),
  next_run_at: (a as any)?.next_run_at || null,
  time_remaining_seconds: (a as any)?.time_remaining_seconds ?? null
      } as SignalRow;
    });

  const signalsData: SignalRow[] = [...liveRows, ...baseSignals];

  // Count all signals (both completed and live)
  const totalSignalsCount = useMemo(
    () => signalsData.length,
    [signalsData]
  );

  // Keep separate count for completed signals only
  const completedSignalsCount = useMemo(
    () => baseSignals.filter(s => effectiveGroup(s, activeRequestIds) === "completed").length,
    [baseSignals, activeRequestIds]
  );

  // Derive credits (UI only – does NOT alter logic)
  const { usedCredits, refundedCredits } = useMemo(() => {
    const u = usageQuery.data;
    if (u) {
      const spent =
        typeof u.spent === "number" ? u.spent :
        typeof u.used === "number" ? u.used : 0;
      const refunded =
        typeof u.refunded === "number" ? u.refunded :
        typeof u.returns === "number" ? u.returns : 0;
      return { usedCredits: spent, refundedCredits: refunded };
    }
    try {
      const txs = transactions;
      const spent = txs
        .filter((t: TransactionItem) => (t.type || "").toUpperCase() === "DEDUCTION")
        .reduce((sum: number, t: TransactionItem) => sum + (t.amount || 0), 0);
      const refunded = txs
        .filter((t: TransactionItem) => (t.type || "").toUpperCase() === "REFUND")
        .reduce((sum: number, t: TransactionItem) => sum + (t.amount || 0), 0);
      return { usedCredits: spent, refundedCredits: refunded };
    } catch {
      return { usedCredits: 0, refundedCredits: 0 };
    }
  }, [usageQuery.data, transactions]);

  const availableCredits =
    (user as any)?.credits_available ??
    (user as any)?.credits ??
    (user as any)?.credit_balance ??
    ((user as any)?.balance) ??
    null;

  const liveAnalysesCount = activeAnalyses.length;

  /* ---------- Derived Growth / Usage Metrics (UI only) ---------- */
  const todayStr = new Date().toISOString().slice(0,10);
  const todaySignals = baseSignals.filter(s => s.created_at.slice(0,10) === todayStr).length;
  const last7Signals = baseSignals.filter(s => {
    const d = s.created_at.slice(0,10);
    const diff = (Date.now() - new Date(d).getTime())/86400000;
    return diff >=0 && diff < 7;
  }).length;
  const prev7Signals = baseSignals.filter(s => {
    const d = s.created_at.slice(0,10);
    const diff = (Date.now() - new Date(d).getTime())/86400000;
    return diff >=7 && diff < 14;
  }).length;
  const growthPct = prev7Signals === 0 ? (last7Signals>0?100:0) : Math.round(((last7Signals - prev7Signals)/Math.max(prev7Signals,1))*100);
  const refundRatio = usedCredits > 0 ? (refundedCredits/usedCredits) : 0;

  // (Removed last sync timer logic per request to hide that UI field)

  const metricsSpark = useMemo(() => {
    const now = new Date();
    const dayMs = 86400000;
    const last7: number[] = [];
    for (let i = 6; i >= 0; i--) {
      const start = new Date(now.getTime() - i * dayMs);
      const dayStr = start.toISOString().slice(0, 10);
      const count = baseSignals.filter(s =>
        s.created_at.slice(0, 10) === dayStr
      ).length;
      last7.push(count);
    }
    const max = Math.max(1, ...last7);
    return { signals: last7, max };
  }, [baseSignals]);

  // Detail data queries (use previously imported hooks)
  const detailId = detail?.id ?? null;
  const safeDetailId = detailId != null && detailId > 0 ? detailId : null;
  const detailApi = useSignalDetail(
    detail && !detail._merged && !detail?._live ? safeDetailId : null
  );
  const liveAnalysisId =
    detail?._live ? (detail.request_id ?? detail.analysis_id ?? 0) : 0;
  const liveDetail = useAnalysisDetail(liveAnalysisId > 0 ? liveAnalysisId : 0);
  const liveReasoning = useLatestReasoning(liveAnalysisId > 0 ? liveAnalysisId : 0);

  // Merge detail once full signal data arrives (non-live)
  useEffect(() => {
    if (!detail || !detailApi.data || detail._merged || detail._live) return;
    const full = detailApi.data;
    const mapPairs: [keyof SignalRow, any, any][] = [
      ["reasoning", detail.reasoning, full.reasoning || detail.reasoning],
      ["entry", detail.entry, full.entry_price ?? detail.entry],
      // Map new fields tp1/tp2; keep legacy single `tp` as fallback
      ["tp1", (detail as any).tp1, (full as any).tp1 ?? full.take_profit ?? (detail as any).tp1],
      ["tp2", (detail as any).tp2, (full as any).tp2 ?? (detail as any).tp2],
      ["sl", detail.sl, full.stop_loss ?? detail.sl],
      ["rr", detail.rr, full.rr_ratio ?? detail.rr],
      ["timeframe", detail.timeframe, full.timeframe ?? detail.timeframe],
    ];
    const updates: Partial<SignalRow> = {};
    for (const [k, prev, next] of mapPairs) {
      if (next !== undefined && next !== null && next !== prev) {
        (updates as any)[k] = next;
      }
    }
    if (Object.keys(updates).length) {
      setDetail(d => (d ? { ...d, ...updates, _merged: true } : d));
    } else {
      setDetail(d => (d ? { ...d, _merged: true } : d));
    }
  }, [detail, detailApi.data]);

  // Fallback reasoning fetch for completed signals without reasoning
  useEffect(() => {
    if (detail && !detail._live && isFailedDashboardStatus(detail.status)) {
      setDetail(null);
      return;
    }
    const fetchLatestReason = async () => {
      if (!detail || detail._live) return;
      if (detail.reasoning?.trim()) return;
      if (detail._loadingDetail || detail._reasonTried) return;
      if (!detail.request_id) return;
      setDetail(d => (d ? { ...d, _loadingDetail: true } : d));
      let foundReason = "";
      try {
        try {
          const r = await apiRequest("GET", `/api/analysis/${detail.request_id}/latest-reasoning`);
            if (r.ok) {
              const data = await r.json();
              foundReason = data.reason || data.full_reasoning || "";
            }
        } catch {}
        if (!foundReason) {
          try {
            const r2 = await apiRequest("GET", `/api/analysis/detail/${detail.request_id}`);
            if (r2.ok) {
              const d2 = await r2.json();
              const fs = d2.final_signal;
              foundReason = fs?.reasoning || fs?.analysis || "";
            }
          } catch {}
        }
      } finally {
        setDetail(d => (d ? {
          ...d,
          reasoning: foundReason || d.reasoning,
          _loadingDetail: false,
          _reasonTried: true
        } : d));
      }
    };
    fetchLatestReason();
  }, [detail]);

  // Refetch signals after a live analysis completes
  useEffect(() => {
    if (detail?._live && liveDetail.data?.status === "COMPLETED") {
      signalsQuery.refetch();
    }
  }, [detail?._live, liveDetail.data?.status, signalsQuery]);

  const filteredSignals = useMemo(() => {
    return signalsData
      .filter(sig => {
        if (isFailedDashboardStatus(sig.status)) return false;
        const sym = (sig.symbol || "").toUpperCase();
        if (search && !sym.includes(search.trim().toUpperCase())) return false;
        if (typeFilter !== "all") {
          const mapped = sig.signalType === "LONG" ? "BULLISH" : sig.signalType === "SHORT" ? "BEARISH" : "NEUTRAL";
          if (mapped !== typeFilter && !sig._live) return false;
        }
        if (!isWithinRange(sig.created_at, timeFilter)) return false;
        return true;
      })
      .sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }, [signalsData, search, typeFilter, timeFilter]);

  const pageLoading = authLoading || signalsLoading;

  if (!pageLoading && signalsError) {
    const errText = typeof (signalsError as any)?.message === "string"
      ? (signalsError as any).message
      : JSON.stringify(signalsError, Object.getOwnPropertyNames(signalsError) as any);
    return (
      <Layout>
        {seoNode}
        <div className="min-h-screen bg-[#0B1020] text-gray-200 p-6">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-[#131C2E]/70 backdrop-blur border border-red-500/50 shadow-lg shadow-red-900/30">
              <CardContent className="p-8 text-center space-y-4">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                <h2 className="text-2xl font-bold text-white">Error Loading Signals</h2>
                <p className="text-sm text-gray-300 break-all">{errText}</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-800/40 hover:brightness-110"
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        {seoNode}
        <div className="min-h-screen flex items-center justify-center bg-[#0B1020]">
          <div className="text-gray-300 text-sm">Redirecting to login...</div>
        </div>
      </Layout>
    );
  }

  const firstName =
    (user as any)?.firstName || (user as any)?.first_name || "Trader";
  const initials = (firstName || "T").charAt(0).toUpperCase();

  const prettyDateTime = (ts: string) => {
    if (!ts) return "—";
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit"
    });
  };

  const splitDateTime = (ts: string) => {
    if (!ts) return { date: "—", time: "—" };
    const d = new Date(ts);
    if (isNaN(d.getTime())) return { date: "—", time: "—" };
    const date = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
    const time = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
    return { date, time };
  };

  const signalStatusLabel = (s: SignalRow) => {
    const g = effectiveGroup(s, activeRequestIds);
    if (g === "live") return "Live";
    if (g === "completed") return "Completed";
    if (g === "in_progress") return "In Progress";
    return (s.status || "—");
  };

  const confidenceValue = (s: SignalRow) => s.confidence ?? null;
  const closeDetail = () => setDetail(null);

  useEffect(() => {
    setDetailExpanded(false);
  }, [detail?.id, detail?.request_id, detail?._live]);

  const detailSignalForCard = useMemo(() => {
    if (!detail || detail._live) return null;
    return {
      ...detail,
      type: detail.signalType,
      signal_type: detail.signalType,
      entry_price: detail.entry,
      tp1: (detail as any).tp1 ?? (detail as any).tp,
      tp2: (detail as any).tp2,
      stop_loss: detail.sl,
      rr_ratio: detail.rr,
      confidence_percent: detail.confidence,
      created_at: detail.created_at,
    };
  }, [detail]);

  // removed unused confidenceColor helper (old table style)

  function statusTone(label: string) {
    const L = label.toLowerCase();
    if (L.includes("fail") || L.includes("error") || L.includes("cancel")) {
      return "bg-red-500/15 border border-red-500/60 text-red-300";
    }
    if (label === "Completed") {
      return "bg-emerald-500/15 border border-emerald-500/60 text-emerald-300";
    }
    if (label === "Live" || label === "Running" || label === "In Progress") {
      return "bg-amber-500/15 border border-amber-500/60 text-amber-300 animate-pulse";
    }
    return "bg-cyan-500/15 border border-cyan-500/50 text-cyan-300";
  }

  const liveReasoningTextRaw = detail?._live
    ? (liveReasoning.data?.reason || detail.reasoning || "")
    : detail?.reasoning || "";
  const liveReasoningText = normalizeDashboardNoTradeReasoning(liveReasoningTextRaw);

  return (
    <Layout>
      {seoNode}
      {/* Skeleton layer while loading */}
      <div className={pageLoading ? "min-h-screen relative overflow-hidden bg-[#070D19] text-gray-200 pb-20 opacity-100 transition-opacity duration-300" : "opacity-0 pointer-events-none h-0"}>
        <div className="relative max-w-7xl mx-auto px-6 pt-6 space-y-8">
          {/* Header skeleton */}
          <div className="rounded-xl border border-[#1E2A44]/70 bg-[#0F1828]/80 backdrop-blur px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <ShimmerBlock className="w-10 h-10 rounded-lg" />
                <div className="flex flex-col gap-2 w-40">
                  <ShimmerBlock className="h-3" />
                  <ShimmerBlock className="h-2 w-24" />
                </div>
              </div>
              <ShimmerBlock className="h-8 w-32" />
            </div>
          </div>

          {/* Market overview skeleton */}
          <ShimmerBlock className="h-28" />

          {/* Metrics skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            <ShimmerBlock className="h-28" />
            <ShimmerBlock className="h-28" />
            <ShimmerBlock className="h-28" />
            <ShimmerBlock className="h-28" />
          </div>

          {/* Filters skeleton */}
          <ShimmerBlock className="h-24" />

          {/* Table skeleton */}
          <div className="bg-[#121B25]/60 backdrop-blur-md border border-[#1E2A44]/70 shadow-xl shadow-black/30 overflow-hidden rounded-xl">
            <div className="p-4 border-b border-[#1E2A44]/70">
              <div className="grid grid-cols-6 gap-4">
                <ShimmerBlock className="h-3" />
                <ShimmerBlock className="h-3" />
                <ShimmerBlock className="h-3" />
                <ShimmerBlock className="h-3" />
                <ShimmerBlock className="h-3" />
                <ShimmerBlock className="h-3" />
              </div>
            </div>
            <div className="p-4 space-y-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="grid grid-cols-6 gap-4 items-center">
                  <ShimmerBlock className="h-5" />
                  <ShimmerBlock className="h-5" />
                  <ShimmerBlock className="h-5" />
                  <ShimmerBlock className="h-5" />
                  <ShimmerBlock className="h-5" />
                  <ShimmerBlock className="h-8" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: pageLoading ? 0 : 1, y: pageLoading ? 4 : 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        className={
          cn(
            "min-h-screen relative overflow-hidden bg-[#070D19] text-gray-200 pb-20",
            pageLoading ? "opacity-0 pointer-events-none" : "opacity-100"
          ) as any
        }
      >
        {/* --- COMPACT HEADER STRIP --- */}
        <div className="relative max-w-7xl mx-auto px-6 pt-6 space-y-8">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#1E2A44]/70 bg-[#0F1828]/80 backdrop-blur px-5 py-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 via-sky-500 to-indigo-600 flex items-center justify-center text-lg font-bold text-white ring-1 ring-cyan-400/40">
                  {initials}
                </div>
                <span className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-[9px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live
                </span>
              </div>
              <div className="flex flex-col leading-tight">
                <h1 className="text-sm md:text-base font-semibold text-white">Welcome back, {firstName}</h1>
                <span className="text-[11px] md:text-[12px] text-gray-400">Real-Time AI Analysis & Data Metric Dashboard</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/5 border border-white/10 text-cyan-300 text-[11px]">
                <Coins className="w-3.5 h-3.5" />
                <span className="font-medium text-white">{availableCredits != null ? availableCredits : (Math.max(0, (availableCredits ?? 0))) || '—'}</span>
              </span>
              <Button
                onClick={goToTrading}
                className="relative overflow-hidden rounded-lg px-4 py-2 text-[12px] font-semibold bg-gradient-to-r from-cyan-400 via-sky-400 to-indigo-500 text-[#06131C] hover:brightness-110 transition-all"
              >
                <span className="flex items-center gap-1.5">New Analysis <ArrowUp className="w-3.5 h-3.5 rotate-45" /></span>
              </Button>
            </div>
          </div>

          {/* --- MARKET OVERVIEW (extracted component) --- */}
          <MarketOverview />

          {/** Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            <StatCard
              label="Total Analyses"
              value={totalSignalsCount}
              icon={<BarChart3 className="w-5 h-5" />}
              accent="cyan"
              spark={metricsSpark.signals}
              extra={
                <GrowthBadge value={growthPct} label="7d" />
              }
            />
            <StatCard
              label="Live Analyses"
              value={liveAnalysesCount}
              icon={<Activity className="w-5 h-5" />}
              accent="amber"
              spark={metricsSpark.signals.map(v => Math.max(0, v - 1))}
              extra={<MiniBadge icon={<Clock className="w-3 h-3" />} text={`${completedSignalsCount} completed`} tone="amber" />}
            />
            <StatCard
              label="Used Tokens"
              value={usedCredits}
              icon={<Coins className="w-5 h-5" />}
              accent="rose"
              spark={metricsSpark.signals.map(v => v + 2)}
              extra={<MiniBadge icon={<TrendingUp className="w-3 h-3" />} text={`${todaySignals} today`} tone="rose" />}
            />
            <StatCard
              label="Returned Tokens"
              value={refundedCredits}
              icon={<RotateCcw className="w-5 h-5" />}
              accent="emerald"
              spark={metricsSpark.signals.slice().reverse()}
              extra={<MiniBadge icon={<Shield className="w-3 h-3" />} text={`${Math.round(refundRatio*100)}% ratio`} tone="emerald" />}
            />
          </div>

          {/** Filters */}
          <Card className="bg-[#121B25]/70 backdrop-blur-md border border-[#1E2A44]/70 shadow-lg shadow-black/30">
            <CardContent className="p-5 space-y-5">
              <div className="flex flex-col lg:flex-row gap-5">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400/70" />
                  <Input
                    placeholder="Search symbol e.g. BTC"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 h-12 bg-[#0F1524]/80 border-[#233044] text-gray-100 placeholder-gray-500 focus:border-cyan-500/70 focus:ring-0 rounded-xl"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-6 w-full lg:w-auto">
                  <div className="flex flex-col gap-2">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Analysis Type</span>
                    <div className="flex flex-wrap gap-2">
                      {(["all", "BULLISH", "BEARISH", "NEUTRAL"] as const).map(v => {
                        const icon = v === "BULLISH" ? <ArrowUp className="w-3 h-3" /> : v === "BEARISH" ? <ArrowDown className="w-3 h-3" /> : v === "NEUTRAL" ? <Minus className="w-3 h-3" /> : <LineChart className="w-3 h-3" />;
                        return (
                          <ChipToggle
                            key={v}
                            active={typeFilter === v}
                            onClick={() => setTypeFilter(v)}
                            label={
                              v === "all" ? "All " :
                              v === "BULLISH" ? "Bullish Bias " :
                              v === "BEARISH" ? "Bearish Trajectory " :
                              "Neutral/No Output "
                            }
                            color={
                              v === "BULLISH" ? "emerald" :
                                v === "BEARISH" ? "rose" :
                                  v === "NEUTRAL" ? "slate" : "cyan"
                            }
                            icon={icon}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Time Range</span>
                    <div className="flex flex-wrap gap-2">
                      {TIME_FILTERS.map(t => {
                        const shortLabel = t.value === "all" ? "ALL" : t.value.toUpperCase();
                        return (
                          <ChipToggle
                            key={t.value}
                            active={timeFilter === t.value}
                            onClick={() => setTimeFilter(t.value as any)}
                            label={shortLabel}
                            color={
                              t.value === "24h" ? "sky" :
                                t.value === "7d" ? "violet" :
                                  t.value === "30d" ? "indigo" : "cyan"
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/** Table */}
          <Card className="bg-[#121B25]/60 backdrop-blur-md border border-[#1E2A44]/70 shadow-xl shadow-black/30 overflow-hidden">
            <CardContent className="p-0">
              <Table className="table-fixed w-full" containerClassName="max-h-[800px] overflow-auto">
                <colgroup>
                  <col className="w-[140px]" />
                  <col className="w-[140px]" />
                  <col className="w-[190px]" />
                  <col className="w-[260px]" />
                  <col className="w-[140px]" />
                  <col className="w-[90px]" />
                </colgroup>
                <TableHeader className="sticky top-0 z-20 bg-gradient-to-r from-[#0F1524] via-[#121E33] to-[#16253C] shadow shadow-black/40">
                  <TableRow className="border-[#1E2A44]">
                    <TableHead className="text-gray-300 font-semibold">Date</TableHead>
                    <TableHead className="text-gray-300 font-semibold">Symbol</TableHead>
                    <TableHead className="text-gray-300 font-semibold">Analysis</TableHead>
                    <TableHead className="text-gray-300 font-semibold">Model Metric / Progress</TableHead>
                    <TableHead className="text-gray-300 font-semibold">Status</TableHead>
                    <TableHead className="text-gray-300 font-semibold text-center">View</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                    {filteredSignals.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="py-16 text-center">
                          <div className="flex flex-col items-center gap-4 text-gray-400">
                            <BarChart3 className="w-10 h-10 text-cyan-500" />
                            <div className="text-sm">
                              {signalsData.length === 0
                                ? "No analyses yet. Start a new analysis."
                                : "No analyses match the current filters."}
                            </div>
                            <Button
                              onClick={goToTrading}
                              className="bg-gradient-to-r from-cyan-500 to-sky-500 text-[#06202B] hover:brightness-110 rounded-xl"
                            >
                              New Analysis
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredSignals.map(sig => {
                      const dt = splitDateTime(sig.created_at);
                      const conf = confidenceValue(sig);
                      const statusLbl = signalStatusLabel(sig);
                      const isLive = sig._live || statusLbl === "Live";
                      const signalBadge =
                        sig.signalType === "LONG"
                          ? "bg-emerald-500/10 border border-emerald-500/60 text-emerald-300"
                          : sig.signalType === "SHORT"
                            ? "bg-rose-500/10 border border-rose-500/60 text-rose-300"
                            : isLive
                              ? "bg-amber-500/10 border border-amber-500/60 text-amber-300"
                              : "bg-slate-500/10 border border-slate-500/50 text-slate-300";
                      const icon =
                        sig.signalType === "LONG" ? <ArrowUp className="w-3 h-3" /> :
                          sig.signalType === "SHORT" ? <ArrowDown className="w-3 h-3" /> :
                            isLive ? <Loader2 className="w-3 h-3 animate-spin" /> :
                              <Minus className="w-3 h-3" />;

                      const liveProg = isLive
                        ? (sig.progress_percent ??
                          activeAnalysesMap.get(sig.request_id || 0)?.progress_percent ??
                          30)
                        : undefined;

                      return (
                        <Fragment key={`${sig.id}_${sig.request_id}_${sig._live ? "live" : "final"}`}>
                          <TableRow
                            className={`h-14 border-[#1E2A44]/70 hover:bg-[#1A283D]/70 transition-colors group ${isLive ? "animate-pulse-[slow]" : ""}`}
                          >
                            <TableCell className="text-gray-300 text-sm">
                              <div className="flex flex-col leading-tight">
                                <span className="text-gray-200 text-[12px] font-medium">{dt.date}</span>
                                <span className="text-gray-400 text-[11px]">{dt.time}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-white font-semibold text-sm tracking-wide">
                              <div className="flex flex-col leading-tight">
                                <span className="relative w-fit">
                                  {sig.symbol}
                                  <span className="absolute -bottom-0.5 left-0 w-full h-px bg-gradient-to-r from-cyan-500/40 to-transparent scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
                                </span>
                                {isLive && (
                                  <span className="mt-1 w-fit text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">
                                    LIVE
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={`font-semibold text-xs px-2.5 py-1 rounded-full flex items-center gap-1 ${signalBadge}`}
                              >
                                {icon}{displaySignalLabel(sig.signalType, isLive)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-gray-200">
                              {isLive ? (
                                <LiveProgressCell
                                  analysisId={sig.request_id || sig.analysis_id}
                                  progress={liveProg}
                                  next_run_at={sig.next_run_at || (activeAnalysesMap.get(sig.request_id || 0) as any)?.next_run_at}
                                />
                              ) : conf != null ? (
                                <div className="w-28">
                                  <div className="flex items-center justify-between text-[10px] text-gray-400 mb-1">
                                    <span>{conf}%</span>
                                  </div>
                                  <div className="h-2 rounded-full bg-[#1C2A3E] overflow-hidden ring-1 ring-[#264361]/60">
                                    <div
                                      className="h-full bg-gradient-to-r from-cyan-400 via-sky-500 to-indigo-500 transition-all"
                                      style={{ width: `${Math.min(conf,100)}%`}}
                                    />
                                  </div>
                                </div>
                              ) : "—"}
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-xs font-semibold ${statusTone(statusLbl === "Live" ? "Running" : (statusLbl === "DEAD" ? "Invalidated" : statusLbl))} px-2.5 py-1`}>
                                {statusLbl === "Live" ? "Running" : (statusLbl === "DEAD" ? "Invalidated" : statusLbl)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => setDetail(sig)}
                                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg
                                        bg-[#1A2537] border border-transparent text-cyan-300
                                        hover:border-cyan-500/60 hover:bg-[#203049]
                                        hover:text-white transition-colors shadow-[0_0_0_0_rgba(0,0,0,0)] hover:shadow-cyan-500/20"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs">View analysis detail</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/** Detail Drawer */}
        {detail && (
          <div
            className="fixed inset-0 z-50 flex"
            onMouseDown={(e) => { if (e.target === e.currentTarget) closeDetail(); }}
          >
            <div className="flex-1 bg-black/40 backdrop-blur-sm" />
            <div className="w-full sm:w-[480px] md:w-[540px] h-full bg-[#0F1828]/95 backdrop-blur-xl border-l border-cyan-500/20 shadow-2xl shadow-black/50 animate-in slide-in-from-right duration-300 relative">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-500 via-sky-400 to-indigo-500" />
              <div className="p-6 flex items-start justify-between">
                <h2 className="text-xl font-bold tracking-tight text-white">
                  {detail._live ? (
                    <>
                      {detail.symbol}{" "}
                      <span className="text-cyan-400">Live Analysis</span>
                    </>
                  ) : (
                    <span className="text-cyan-400">Analysis Insight</span>
                  )}
                </h2>
                <button
                  aria-label="Close"
                  className="text-gray-400 hover:text-white transition-colors"
                  onClick={closeDetail}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="px-6 space-y-6 pb-32 overflow-y-auto h-[calc(100%-4rem)]">
                {detail._live ? (
                  <>
                    <div className="flex flex-wrap gap-3 text-[11px] text-gray-400">
                      <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10">
                        AN #{detail.request_id || detail.id}
                      </span>
                      <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10">
                        {prettyDateTime(detail.created_at)}
                      </span>
                      <span className="px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300">
                        Live
                      </span>
                    </div>

                    {/* Simplified: for live analyses show only reasoning */}
                    <div className="space-y-3">
                      {(detail._live && !liveReasoning.data && !liveReasoningText) ||
                        (detail._loadingDetail && !liveReasoningText) ? (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                        </div>
                      ) : null}
                      <div className="overflow-y-auto pr-2 rounded-lg border border-white/10 bg-white/5 transition-all min-h-[20rem] max-h-[70vh] md:max-h-[65vh] p-5 md:p-6">
                        <ReasoningStructuredView
                          text={liveReasoningText}
                          sectionLabels={getReasoningSectionLabels(liveReasoning.data?.phase_number)}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <DayAnalysisSignalCard
                    analysisId={(detail.request_id || detail.analysis_id) as any}
                    signal={detailSignalForCard}
                    expanded={detailExpanded}
                    onExpand={setDetailExpanded}
                  />
                )}

                {/* Price Snapshot section removed per request */}
              </div>
              <div className="absolute bottom-0 inset-x-0 p-5 bg-[#0F1828]/95 border-t border-cyan-500/10 flex justify-end gap-3">
                <Button
                  variant="ghost"
                  className="text-gray-400 hover:text-white hover:bg-white/5 rounded-lg"
                  onClick={closeDetail}
                >
                  Close
                </Button>
                <Button
                  onClick={goToTrading}
                  className="bg-gradient-to-r from-cyan-500 to-sky-500 text-[#06202B] hover:brightness-110 rounded-lg"
                >
                  New Analysis
                </Button>
              </div>
            </div>
          </div>
        )}
  {/* Footer info bar removed per request */}
      </motion.div>
    </Layout>
  );
}

/* ---------- Day Analysis Card (StartTrading parity) ---------- */
const formatLocal = (ts?: string | null) => ts ? new Date(ts).toLocaleString() : '—';
const formatMoney = (v: any) => {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  if (n >= 100) return n.toFixed(2);
  if (n >= 1) return n.toFixed(3);
  return n.toFixed(5);
};

function DayAnalysisSignalCard(props: {
  analysisId?: number;
  signal?: any;
  expanded: boolean;
  onExpand: (e: boolean) => void;
}) {
  const { analysisId, signal, expanded, onExpand } = props;
  const hasAnalysis = typeof analysisId === 'number' && analysisId > 0;
  const detailQuery = useAnalysisDetail(hasAnalysis ? analysisId : 0);
  const latestReasoningQuery = useLatestReasoning(hasAnalysis ? analysisId : 0);
  const detail = detailQuery.data;
  const latestReasoning = latestReasoningQuery.data;

  // Until backend returns fully, keep the View drawer in a loading state (no partial card).
  const waitingBackend = hasAnalysis && (!detailQuery.isFetched || !latestReasoningQuery.isFetched);

  const final: any = useMemo(() => {
    const base = signal || {};
    const nested = base.final_signal && typeof base.final_signal === 'object' ? base.final_signal : null;
    const detailFs = detail && (detail as any).final_signal ? (detail as any).final_signal : null;
    if (detailFs) return { ...base, ...detailFs };
    if (nested) return { ...base, ...nested };
    if (detailFs == null && !signal && detail) return detail;
    return base;
  }, [signal, detail]);

  const reasoningTextRaw: string | undefined = (latestReasoning as any)?.reason || final.reason || final.reasoning;
  const reasoningText: string | undefined = reasoningTextRaw != null
    ? normalizeDashboardNoTradeReasoning(reasoningTextRaw)
    : undefined;

  const marketHealth: any = (final as any)?.market_health_summary ?? (latestReasoning as any)?.market_health_summary ?? (detail as any)?.market_health_summary ?? null;
  const summaryStatus = (marketHealth?.status && String(marketHealth.status).trim()) ? String(marketHealth.status).trim() : undefined;
  const summaryContext = (marketHealth?.context && String(marketHealth.context).trim()) ? String(marketHealth.context).trim() : undefined;
  const summaryExecutionNote = (marketHealth?.execution_note && String(marketHealth.execution_note).trim()) ? String(marketHealth.execution_note).trim() : undefined;

  const isNoTrade = useMemo(() => {
    const candidates = [final, signal, signal?.final_signal, detail, detail && (detail as any).final_signal].filter(Boolean);
    const types = candidates.map((c: any) => (c.type || c.signal_type || c.action || '').toString().toUpperCase());
    if (types.some(t => t === 'LONG' || t === 'SHORT')) return false;
    if (candidates.some((c: any) => c.no_trade === true)) return true;
    if (types.some(t => t === 'NO_TRADE')) return true;
    return false;
  }, [final, signal, detail]);

  function deriveEffectiveSignalType(obj: any): string | undefined {
    if (!obj) return undefined;
    const raw = (obj.type || obj.signal_type || obj.action || '').toString().toUpperCase();
    if (raw === 'NO_TRADE') return 'NO_TRADE';
    if (raw === 'LONG' || raw === 'SHORT') return raw;
    const buySell = raw.includes('BUY') ? 'LONG' : raw.includes('SELL') ? 'SHORT' : undefined;
    let fromPrices: string | undefined;
    if (obj.entry_price && obj.stop_loss) {
      const e = Number(obj.entry_price), sl = Number(obj.stop_loss);
      const t1 = obj.tp1 != null ? Number(obj.tp1) : (obj.take_profit != null ? Number(obj.take_profit) : NaN);
      const t2 = obj.tp2 != null ? Number(obj.tp2) : NaN;
      const bestUp = Math.max(isFinite(t1) ? t1 : -Infinity, isFinite(t2) ? t2 : -Infinity);
      const bestDn = Math.min(isFinite(t1) ? t1 : Infinity, isFinite(t2) ? t2 : Infinity);
      if (isFinite(e) && isFinite(sl)) {
        if (isFinite(bestUp) && bestUp > e && sl < e) fromPrices = 'LONG';
        else if (isFinite(bestDn) && bestDn < e && sl > e) fromPrices = 'SHORT';
      }
    }
    const r = (reasoningText || '').toUpperCase();
    const kw = r.includes('LONG') ? 'LONG' : r.includes('SHORT') ? 'SHORT' : undefined;
    return buySell || fromPrices || kw || undefined;
  }
  const signalType = isNoTrade ? 'NO_TRADE' : deriveEffectiveSignalType(final);
  const marketBias = signalType === 'LONG' ? 'BULLISH' : signalType === 'SHORT' ? 'BEARISH' : signalType;

  const confidencePercentRaw = (final as any)?.confidence_percent ?? (final as any)?.confidence ?? (signal as any)?.confidence_percent ?? (detail as any)?.confidence_percent;
  const confidencePercent = Number(confidencePercentRaw);
  const confidenceDisplay = useMemo(() => {
    if (!isFinite(confidencePercent)) return null;
    if (confidencePercent === 0) {
      return { label: 'No Action', className: 'text-red-200 border-red-500/40 bg-red-500/10', kind: 'no_action' as const };
    }
    if (confidencePercent >= 75 && confidencePercent <= 85) {
      return { label: 'High Confidence', className: 'text-emerald-200 border-emerald-500/40 bg-emerald-500/10', kind: 'high' as const };
    }
    if (confidencePercent >= 65 && confidencePercent <= 74) {
      return { label: 'Medium Confidence', className: 'text-amber-200 border-amber-500/40 bg-amber-500/10', kind: 'medium' as const };
    }
    if (confidencePercent >= 60 && confidencePercent <= 64) {
      return { label: 'Low Confidence', className: 'text-slate-200 border-slate-500/40 bg-slate-500/10', kind: 'low' as const };
    }
    return null;
  }, [confidencePercent]);

  const confidenceTooltip = useMemo(() => {
    const kind = confidenceDisplay?.kind;
    if (!kind) return undefined;
    if (kind === 'high') return 'High (75–85%): Strong structural alignment and clean confirmation.';
    if (kind === 'medium') return 'Medium (65–74%): Directional alignment is valid, conditions are not perfect.';
    if (kind === 'low') return 'Low (60–64%): Fragile conditions, conservative approach required.';
    if (kind === 'no_action') return 'No Action (0%): Conditions are not suitable for deployment at this time.';
    return undefined;
  }, [confidenceDisplay?.kind]);

  const isNoAction = isNoTrade || confidenceDisplay?.kind === 'no_action';

  const referencePrice =
    (final as any)?.reference_price ??
    (final as any)?.referencePrice ??
    final.entry_price ??
    (final as any)?.entryPrice ??
    (final as any)?.entry ??
    (detail as any)?.reference_price ??
    (detail as any)?.referencePrice ??
    (detail as any)?.entry_price ??
    (detail as any)?.entryPrice ??
    (detail as any)?.entry;
  const primaryObjective =
    final.tp1 ??
    (final as any)?.TP1 ??
    (final as any)?.take_profit ??
    (final as any)?.takeProfit ??
    (final as any)?.primary_objective ??
    (final as any)?.primaryObjective;
  const extendedObjective =
    final.tp2 ??
    (final as any)?.TP2 ??
    (final as any)?.extended_objective ??
    (final as any)?.extendedObjective;
  const structuralInvalidation =
    (final as any)?.invalidation_level ??
    (final as any)?.invalidationLevel ??
    final.stop_loss ??
    (final as any)?.stopLoss ??
    (final as any)?.sl ??
    (detail as any)?.invalidation_level ??
    (detail as any)?.invalidationLevel ??
    (detail as any)?.stop_loss ??
    (detail as any)?.stopLoss ??
    (detail as any)?.sl;
  const executionContextRaw =
    (final as any)?.execution_context ??
    (final as any)?.executionContext ??
    final.entry_timing ??
    (final as any)?.entryTiming ??
    'Immediate / Active Market';

  const executionContext = useMemo(() => {
    const v = String(executionContextRaw || '').trim();
    if (!v) return '—';
    if (v === 'Immediate / Active Market') return 'Immediate Market';
    return v;
  }, [executionContextRaw]);

  const rrRatio = useMemo(() => {
    const rrFromApi = Number((final as any)?.rr_ratio ?? (final as any)?.rrRatio);
    if (isFinite(rrFromApi) && rrFromApi > 0) return rrFromApi;

    const rrText = (final as any)?.risk_reward ?? (final as any)?.riskReward ?? (final as any)?.rr ?? (final as any)?.riskRewardRatio;
    if (typeof rrText === 'string' && rrText.trim()) {
      const m = rrText.match(/([0-9]+(?:\.[0-9]+)?)/);
      if (m) {
        const parsed = Number(m[1]);
        if (isFinite(parsed) && parsed > 0) return parsed;
      }
    }

    const ref = Number(referencePrice);
    const inv = Number(structuralInvalidation);
    const tp1 = Number(primaryObjective);
    if (!isFinite(ref) || !isFinite(inv) || !isFinite(tp1)) return null;

    const bias = (marketBias || '').toString().toUpperCase();
    if (bias === 'BULLISH') {
      const risk = ref - inv;
      const reward = tp1 - ref;
      if (!(risk > 0) || !(reward > 0)) return null;
      return reward / risk;
    }
    if (bias === 'BEARISH') {
      const risk = inv - ref;
      const reward = ref - tp1;
      if (!(risk > 0) || !(reward > 0)) return null;
      return reward / risk;
    }
    return null;
  }, [final, referencePrice, structuralInvalidation, primaryObjective, marketBias]);

  const marketBiasText = useMemo(() => {
    if (isNoAction) return 'NO ACTION';
    const v = (marketBias || '').toString().toUpperCase();
    return (v === 'BULLISH' || v === 'BEARISH') ? v : '—';
  }, [marketBias, isNoAction]);

  const symbolText = String((final.symbol || signal?.symbol || '')).toUpperCase() || '—';
  const idText = `#${analysisId || final.id || '—'}`;
  const createdText = formatLocal(final.created_at || final.generated_at || (detail as any)?.created_at);

  const structureBadge = useMemo(() => {
    if (marketBiasText === 'BEARISH') return { label: 'BEARISH MARKET STRUCTURE', className: 'border-red-500/40 text-red-200 bg-[#101E2C]' };
    if (marketBiasText === 'BULLISH') return { label: 'BULLISH MARKET STRUCTURE', className: 'border-emerald-500/40 text-emerald-200 bg-[#101E2C]' };
    if (marketBiasText === 'NO ACTION') return { label: 'NO ACTION', className: 'border-red-500/40 text-red-200 bg-[#101E2C]' };
    return { label: 'MARKET STRUCTURE', className: 'border-[#2A3B57] text-[#C9D3DF] bg-[#101E2C]' };
  }, [marketBiasText]);

  const tokenBadge = useMemo(() => {
    const usedRaw = (signal as any)?.used_credits ?? (final as any)?.used_credits ?? (detail as any)?.used_credits;
    const used = Number(usedRaw);
    if (Number.isFinite(used)) {
      if (used <= 0) {
        return {
          text: 'Refund Token: 1',
          className: 'bg-[#32151A]/70 border border-[#5A2531] text-[#F5C4CC]'
        };
      }
      return {
        text: `Token Used: ${Math.max(0, Math.round(used))}`,
        className: 'bg-[#132F29]/70 border border-[#1F5A41] text-emerald-200'
      };
    }

    const creditsStatus = String((detail as any)?.credits_status ?? (signal as any)?.credits_status ?? (final as any)?.credits_status ?? '').toUpperCase();
    if (creditsStatus === 'RELEASED' || creditsStatus === 'REFUNDED') {
      return {
        text: 'Refund Token: 1',
        className: 'bg-[#32151A]/70 border border-[#5A2531] text-[#F5C4CC]'
      };
    }

    return {
      text: 'Token Used: 1',
      className: 'bg-[#132F29]/70 border border-[#1F5A41] text-emerald-200'
    };
  }, [signal, final, detail]);

  const loadingSkeleton = (
    <div className="mx-auto w-full max-w-[460px] p-4 md:p-5 rounded-xl border border-[#223047] bg-gradient-to-b from-[#0D1426]/90 to-[#0B1320]/80 sm:backdrop-blur-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <ShimmerBlock className="h-6 w-32" />
          <div className="mt-2 flex flex-wrap gap-2">
            <ShimmerBlock className="h-3 w-24" />
            <ShimmerBlock className="h-3 w-40" />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <ShimmerBlock className="h-7 w-24 rounded-lg" />
          <ShimmerBlock className="h-7 w-40 rounded-md" />
        </div>
      </div>

      <div className="w-full grid grid-cols-2 gap-2 mb-4 items-stretch">
        <ShimmerBlock className="h-[54px] rounded-xl" />
        <ShimmerBlock className="h-[54px] rounded-xl" />
        <ShimmerBlock className="h-[54px] rounded-xl" />
        <ShimmerBlock className="h-[54px] rounded-xl" />
        <ShimmerBlock className="h-[54px] rounded-xl" />
        <ShimmerBlock className="h-[54px] rounded-xl" />
      </div>

      <div className="mt-3 rounded-lg border border-[#223047] bg-[#0B162A]/60 px-2.5 py-2">
        <ShimmerBlock className="h-4 w-40" />
        <div className="mt-2">
          <ShimmerBlock className="h-3 w-72" />
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-[#223047] bg-[#0B162A]/60 p-2.5 md:p-3 space-y-3">
        <div>
          <ShimmerBlock className="h-3 w-28" />
          <div className="mt-2">
            <ShimmerBlock className="h-3 w-full" />
          </div>
        </div>
        <div>
          <ShimmerBlock className="h-3 w-32" />
          <div className="mt-2">
            <ShimmerBlock className="h-3 w-5/6" />
          </div>
        </div>
        <div>
          <ShimmerBlock className="h-3 w-32" />
          <div className="mt-2">
            <ShimmerBlock className="h-3 w-4/6" />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <ShimmerBlock className="h-10 w-44 rounded-md" />
      </div>
    </div>
  );

  const card = (
    <div className="mx-auto w-full max-w-[460px] p-4 md:p-5 rounded-xl border border-[#223047] bg-gradient-to-b from-[#0D1426]/90 to-[#0B1320]/80 sm:backdrop-blur-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-lg md:text-xl tracking-tight truncate">{symbolText}</div>
          <div className="mt-1 text-[12px] text-[#7F8B99] font-medium flex flex-wrap gap-2">
            <span>ID: {idText}</span>
            <span className="opacity-60">•</span>
            <span>{createdText}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className={cn("px-2.5 py-1 rounded-lg text-[12px] font-semibold", tokenBadge.className)}>
            {tokenBadge.text}
          </div>
          {!isNoAction ? (
            <div className={`px-2 py-1 rounded-md text-[12px] font-semibold border ${structureBadge.className}`}>{structureBadge.label}</div>
          ) : null}
        </div>
      </div>

      {isNoAction && (
        <div className="mb-4">
          <div className="mx-auto w-full rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-center">
            <div className="text-red-300 font-extrabold tracking-wide text-base md:text-lg">NO ACTION</div>
          </div>
        </div>
      )}

      {!isNoAction && (
        <div className="w-full grid grid-cols-2 gap-2 mb-4 items-stretch">
          <DayAnalysisFieldBadge icon="📈" label="Data Point: Initiation" value={formatMoney(referencePrice)} color="blue" />
          <DayAnalysisFieldBadge icon="🎯" label="Target 1 (Closer)" value={formatMoney(primaryObjective)} color="green" copyText={isFinite(Number(primaryObjective)) ? String(primaryObjective) : undefined} />
          <DayAnalysisFieldBadge icon="🎯" label="Target 2 (Further)" value={formatMoney(extendedObjective)} color="green" copyText={isFinite(Number(extendedObjective)) ? String(extendedObjective) : undefined} />
          <DayAnalysisFieldBadge icon="⛔" label="Risk Management Parameter" value={formatMoney(structuralInvalidation)} color="red" copyText={isFinite(Number(structuralInvalidation)) ? String(structuralInvalidation) : undefined} />
          <DayAnalysisFieldBadge icon="⚖️" label="Pre-defined Closure Strategy (R/R)" value={isFinite(Number(rrRatio)) ? `${Number(rrRatio).toFixed(2)}` : '—'} color="purple" />
          <DayAnalysisFieldBadge icon="🚀" label="Execution Context" value={executionContext || '—'} color="neutral" />
        </div>
      )}

      {!isNoAction && confidenceDisplay && (
        <div className="mt-3 rounded-lg border border-[#223047] bg-[#0B162A]/60 px-2.5 py-2">
          <div title={confidenceTooltip} className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${confidenceDisplay.className}`}>
            {confidenceDisplay.label} · {Number.isFinite(confidencePercent) ? `${Math.round(confidencePercent)}%` : '—'}
          </div>
          <div className="mt-0.5 text-[10px] text-[#A7B0C0]">
            Confidence reflects structural alignment and execution conditions, not certainty.
          </div>
        </div>
      )}

      <div className="mt-3 rounded-xl border border-[#223047] bg-[#0B162A]/60 p-2.5 md:p-3">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8B94A7]">MARKET STATUS</div>
        <div className="mt-1 text-[12px] md:text-[13px] text-[#D0D9E2] leading-snug">{summaryStatus || '—'}</div>

        <div className="mt-3 text-[10px] uppercase tracking-wider font-semibold text-[#8B94A7]">MARKET CONTEXT</div>
        <div className="mt-1 text-[12px] md:text-[13px] text-[#A7B0C0] leading-snug">{summaryContext || '—'}</div>

        <div className="mt-3 text-[10px] uppercase tracking-wider font-semibold text-[#8B94A7]">EXECUTION NOTE</div>
        <div className="mt-1 text-[12px] md:text-[13px] text-[#97A6BA] leading-snug">{summaryExecutionNote || '—'}</div>
      </div>

      {reasoningText && (
        <div className="mt-3">
          <Button className="border border-[#2A3B57] bg-[#0F1B2B]/40 text-white hover:border-[#00D1FF]" onClick={() => onExpand(!expanded)}>
            <Eye className="w-4 h-4 mr-2" />
            {expanded ? 'Hide Full Reasoning' : 'View Full Reasoning'}
          </Button>
        </div>
      )}

      <AnimatePresence>
        {expanded && reasoningText && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 p-4 rounded-xl bg-[#0B162A] border border-[#223047] text-[12px] md:text-sm leading-relaxed space-y-3 shadow-inner"
          >
            <ReasoningStructuredView
              text={reasoningText}
              sectionLabels={getReasoningSectionLabels(latestReasoning?.phase_number)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return waitingBackend ? loadingSkeleton : card;
}

interface DayAnalysisFieldBadgeProps {
  label: string;
  value: React.ReactNode;
  copyText?: string;
  color?: 'neutral' | 'green' | 'red' | 'purple' | 'blue' | 'cyan';
  icon?: string;
}

function DayAnalysisFieldBadge({ label, value, color = 'neutral', icon, copyText }: DayAnalysisFieldBadgeProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const colorMap: Record<NonNullable<DayAnalysisFieldBadgeProps['color']>, string> = {
    neutral: 'bg-[#142332] border-[#1F3347] text-[#D0D9E2]',
    green: 'bg-[#132F29] border-[#1F5A41] text-[#CDEEDD]',
    red: 'bg-[#32151A] border-[#5A2531] text-[#F5C4CC]',
    purple: 'bg-[#241B36] border-[#3D2A5A] text-[#E3D4F9]',
    blue: 'bg-[#142841] border-[#1E4563] text-[#D0E6F5]',
    cyan: 'bg-[#123640] border-[#1F5A63] text-[#CDEFF5]',
  };
  const canCopy = typeof copyText === 'string' && copyText.trim() !== '';

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const onCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(copyText!);
      setCopied(true);
      toast({ title: 'Copied', description: `${label} copied to clipboard.` });
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  return (
    <div className={`flex flex-col gap-1 p-2 rounded-xl border ${colorMap[color]} relative overflow-hidden`}>
      <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 flex items-center gap-1 pr-5">
        {icon && <span className="text-[11px]">{icon}</span>}
        {label}
      </span>
      <span className="text-[12px] font-bold font-mono leading-none">{value}</span>
      {canCopy && (
        <button
          type="button"
          onClick={onCopy}
          aria-label={`Copy ${label}`}
          title={`Copy ${label}`}
          className="absolute top-1.5 right-1.5 p-1 rounded-md text-[#97A6BA] hover:text-white hover:bg-white/10 transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      )}
      <span className="pointer-events-none absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.12),transparent_65%)]" />
    </div>
  );
}

/* ---------- LiveProgressCell Component (StartTrading parity) ---------- */
function LiveProgressCell({ analysisId, progress, next_run_at }: { analysisId?: number | null; progress?: number; next_run_at?: string | null }) {
  const pct = Math.min(Math.max(progress ?? 0, 0), 100);
  const { data: reasoning } = useLatestReasoning(analysisId && analysisId > 0 ? analysisId : 0);
  const timeRemainingInitial = useMemo(() => {
    if (reasoning?.time_remaining_seconds != null) return reasoning.time_remaining_seconds;
    if (next_run_at) {
      const next = new Date(next_run_at).getTime();
      return Math.max(0, Math.floor((next - Date.now()) / 1000));
    }
    return 0;
  }, [reasoning, next_run_at]);
  const [countdown, setCountdown] = useState(timeRemainingInitial);
  useEffect(() => setCountdown(timeRemainingInitial), [timeRemainingInitial]);
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown]);
  const mmss = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2,'0')}:${String(sec % 60).padStart(2,'0')}`;
  const showTimer = countdown > 0 && pct < 100;
  return (
    <div className="w-40">
      <div className="flex items-center justify-between text-[10px] mb-1">
        <span className="text-amber-300 font-medium">{pct}%</span>
        {showTimer && (
          <span className="flex items-center gap-1 text-cyan-300 font-mono">
            <span className="uppercase tracking-wide text-cyan-200/60">Next</span>
            {mmss(countdown)}
          </span>
        )}
      </div>
      <div className="h-2 rounded-full bg-[#233044] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ---------- Small Components ---------- */
function StatCard(p: { label: string; value: number; icon: React.ReactNode; accent?: "cyan"|"amber"|"rose"|"emerald"; spark?: number[]; extra?: React.ReactNode }) {
  const { label, value, icon, accent = "cyan", spark = [], extra } = p;

  const accentMap: Record<string, { ring: string; grad: string; icon: string; glow: string; }> = {
    cyan: { ring: "ring-cyan-500/30", grad: "from-cyan-500/20 via-sky-500/10 to-transparent", icon: "bg-cyan-500/15 text-cyan-300", glow: "shadow-cyan-500/30" },
    amber: { ring: "ring-amber-400/30", grad: "from-amber-400/20 via-yellow-400/10 to-transparent", icon: "bg-amber-400/15 text-amber-300", glow: "shadow-amber-400/30" },
    rose: { ring: "ring-rose-500/30", grad: "from-rose-500/20 via-pink-500/10 to-transparent", icon: "bg-rose-500/15 text-rose-300", glow: "shadow-rose-500/30" },
    emerald: { ring: "ring-emerald-500/30", grad: "from-emerald-500/20 via-green-500/10 to-transparent", icon: "bg-emerald-500/15 text-emerald-300", glow: "shadow-emerald-500/30" }
  };
  const a = accentMap[accent];

  return (
    <div
      className={`relative group rounded-2xl p-5 bg-[#131C2E]/75 backdrop-blur-md border border-[#1E2A44]/70
        ring-1 ${a.ring} overflow-hidden
        transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_0_30px_-8px_rgba(56,189,248,0.45)]`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${a.grad} opacity-70`} />
      <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-30 bg-gradient-to-br from-white/5 to-transparent" />
      <div className="relative flex items-start justify-between gap-4">
        <div className="space-y-4 flex-1">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center border border-white/10 backdrop-blur ${a.icon}`}>
              {icon}
            </div>
            <div className="flex flex-col">
              <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold flex items-center gap-2">
                {label}
                {extra && <span className="inline-flex">{extra}</span>}
              </div>
              <div className="text-2xl font-bold text-white">{value}</div>
            </div>
          </div>
          <div className="h-10">
            <Sparkline data={spark} color="stroke-white/70" gradientId={`grad-${label.replace(/\s+/g,'')}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Micro Badges for Stat Cards ---------- */
function GrowthBadge({ value, label }: { value: number; label: string }) {
  const tone = value > 0 ? "text-emerald-300" : value < 0 ? "text-rose-300" : "text-gray-300";
  const sign = value > 0 ? "+" : "";
  return (
    <span className={`text-[10px] font-semibold ${tone}`}>{sign}{value}% {label}</span>
  );
}
function MiniBadge({ icon, text, tone }: { icon: React.ReactNode; text: string; tone: "amber"|"rose"|"emerald"|"cyan" }) {
  const toneMap: Record<string,string> = {
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    rose: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    cyan: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30"
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${toneMap[tone]}`}>{icon}{text}</span>;
}

function ChipToggle({
  active, onClick, label, color, icon
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  color?: "cyan"|"emerald"|"rose"|"slate"|"amber"|"sky"|"violet"|"indigo";
  icon?: React.ReactNode;
}) {
  const palettes: Record<string, { on: string; off: string; hover: string; ring: string; }> = {
    cyan: { on: "bg-cyan-500/20 text-cyan-300 border-cyan-500/50", off: "text-cyan-300/70 border-cyan-500/30", hover: "hover:bg-cyan-500/15", ring: "focus:ring-cyan-500/50" },
    emerald: { on: "bg-emerald-500/20 text-emerald-300 border-emerald-500/50", off: "text-emerald-300/70 border-emerald-500/30", hover: "hover:bg-emerald-500/15", ring: "focus:ring-emerald-500/50" },
    rose: { on: "bg-rose-500/20 text-rose-300 border-rose-500/50", off: "text-rose-300/70 border-rose-500/30", hover: "hover:bg-rose-500/15", ring: "focus:ring-rose-500/50" },
    slate: { on: "bg-slate-500/20 text-slate-200 border-slate-400/50", off: "text-slate-300/70 border-slate-500/30", hover: "hover:bg-slate-500/15", ring: "focus:ring-slate-500/50" },
    amber: { on: "bg-amber-500/25 text-amber-200 border-amber-400/50", off: "text-amber-300/70 border-amber-500/30", hover: "hover:bg-amber-500/15", ring: "focus:ring-amber-500/50" },
    sky: { on: "bg-sky-500/25 text-sky-200 border-sky-400/50", off: "text-sky-300/70 border-sky-500/30", hover: "hover:bg-sky-500/15", ring: "focus:ring-sky-500/50" },
    violet: { on: "bg-violet-500/25 text-violet-200 border-violet-400/50", off: "text-violet-300/70 border-violet-500/30", hover: "hover:bg-violet-500/15", ring: "focus:ring-violet-500/50" },
    indigo: { on: "bg-indigo-500/25 text-indigo-200 border-indigo-400/50", off: "text-indigo-300/70 border-indigo-500/30", hover: "hover:bg-indigo-500/15", ring: "focus:ring-indigo-500/50" },
  };
  const c = palettes[color || "cyan"];
  return (
    <button
      onClick={onClick}
      className={`px-4 h-10 rounded-full border text-sm font-medium backdrop-blur
        transition-all ${active ? c.on : c.off} ${c.hover} ${c.ring}
        focus:outline-none focus:ring-2 focus:ring-offset-0`}
    >
  <span className="flex items-center gap-1">{icon}{label}</span>
    </button>
  );
}

function Sparkline({ data, color, gradientId }: { data: number[]; color: string; gradientId: string; }) {
  if (!data || data.length === 0) {
    return <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-500">—</div>;
  }
  const max = Math.max(...data, 1);
  const w = 120;
  const h = 40;
  const step = w / (data.length - 1);
  const d = data
    .map((v, i) => {
      const x = i * step;
      const y = h - (v / max) * (h - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const area = `${d} L ${w},${h} L 0,${h} Z`;

    return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full overflow-visible">
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} className="opacity-70" />
      <path d={d} className={`${color} drop-shadow-[0_0_2px_rgba(255,255,255,0.4)]`} fill="none" strokeWidth={2} strokeLinecap="round" />
      {data.map((v, i) => {
        const x = i * step;
        const y = h - (v / max) * (h - 4) - 2;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={2.2}
            className="fill-white/80 stroke-[#0D1628] stroke-[1.5]"
          />
        );
      })}
    </svg>
  );
}